/**
 * AI Clinical Note Check via Medplum Bot (`ai-clinical-note-check`).
 */

import { ContentType, getStatus, OperationOutcomeError, operationOutcomeToString } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier, QuestionnaireResponse } from '@medplum/fhirtypes';

export const AI_NOTE_CHECK_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'ai-clinical-note-check',
} as const;

let cachedAiNoteCheckBotId: string | undefined;

export function getAiNoteCheckBotExecuteTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_AI_NOTE_CHECK_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_AI_NOTE_CHECK_BOT_IDENTIFIER_SYSTEM?.trim() ??
    AI_NOTE_CHECK_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_AI_NOTE_CHECK_BOT_IDENTIFIER_VALUE?.trim() ??
    AI_NOTE_CHECK_BOT_IDENTIFIER.value;
  return { system, value };
}

/**
 * Prefer Bot/{id}/$execute (see adminActionsService). Identifier-based $execute can 400 locally.
 */
export async function resolveAiNoteCheckBotExecuteTarget(
  medplum: MedplumClient,
): Promise<string | undefined> {
  const configured = import.meta.env.VITE_MEDPLUM_AI_NOTE_CHECK_BOT_ID?.trim();
  if (configured) return configured;
  if (cachedAiNoteCheckBotId) return cachedAiNoteCheckBotId;

  const fallback = getAiNoteCheckBotExecuteTarget();
  if (typeof fallback === 'string') {
    cachedAiNoteCheckBotId = fallback;
    return fallback;
  }

  try {
    const bots = await medplum.searchResources('Bot', {
      identifier: `${fallback.system}|${fallback.value}`,
      _count: '1',
    });
    const id = bots[0]?.id;
    if (id) cachedAiNoteCheckBotId = id;
    return id;
  } catch {
    return undefined;
  }
}

/** True when the bot is missing or not executable (local dev without registration). */
export function isAiNoteCheckBotUnavailableError(err: unknown): boolean {
  if (!(err instanceof OperationOutcomeError)) return false;
  const status = getStatus(err.outcome);
  const message = operationOutcomeToString(err.outcome).toLowerCase();
  if (status === 404) return true;
  if (status === 400) {
    return (
      message.includes('not found') ||
      message.includes('unknown') ||
      message.includes('no bot') ||
      message.includes('no executable code') ||
      message.includes('invalid')
    );
  }
  return false;
}

export function isAiNoteCheckForbiddenError(err: unknown): boolean {
  if (!(err instanceof OperationOutcomeError)) return false;
  const status = getStatus(err.outcome);
  const message = operationOutcomeToString(err.outcome).toLowerCase();
  return status === 403 || (status === 400 && message.includes('forbidden'));
}

export async function isAiNoteCheckBotAvailable(medplum: MedplumClient): Promise<boolean> {
  return Boolean(await resolveAiNoteCheckBotExecuteTarget(medplum));
}

/** Cap bot execution so Review & Sign cannot spin forever when the bot/LLM stalls. */
const AI_NOTE_CHECK_TIMEOUT_MS = 20_000;

export async function requestAiNoteCheck(
  medplum: MedplumClient,
  questionnaireResponseId: string,
  options?: { force?: boolean; reason?: 'post-save' | 'manual' },
): Promise<QuestionnaireResponse> {
  const botId = await resolveAiNoteCheckBotExecuteTarget(medplum);
  if (!botId) {
    throw new OperationOutcomeError({
      resourceType: 'OperationOutcome',
      issue: [{ severity: 'error', code: 'not-found', details: { text: 'AI note check bot not found' } }],
    });
  }

  const result = await Promise.race([
    medplum.executeBot(
      botId,
      {
        questionnaireResponseId,
        force: options?.force,
        reason: options?.reason ?? 'post-save',
      },
      ContentType.JSON,
    ),
    new Promise<never>((_, reject) => {
      window.setTimeout(() => {
        reject(new Error(`AI note check timed out after ${AI_NOTE_CHECK_TIMEOUT_MS / 1000}s`));
      }, AI_NOTE_CHECK_TIMEOUT_MS);
    }),
  ]);
  if (!result || typeof result !== 'object' || (result as QuestionnaireResponse).resourceType !== 'QuestionnaireResponse') {
    throw new Error('AI note check bot did not return a QuestionnaireResponse');
  }
  return result as QuestionnaireResponse;
}
